import { useEffect, useState } from "react";
import { convertHEIC, isHEIC } from "@tellescope/utils";

/**
 * Resolves an object URL for previewing a locally-selected file, decoding HEIC/HEIF first since
 * browsers cannot render those natively. Returns null when the file has no previewable image
 * (a non-image file, or a HEIC that failed to decode).
 *
 * The URL is revoked automatically when the file changes or the component unmounts.
 *
 * @example
 * ```tsx
 * const previewUrl = useFileLocalPreviewUrl(file);
 * return previewUrl ? <img src={previewUrl} alt={file.name} /> : <FileIcon />;
 * ```
 */
export const useFileLocalPreviewUrl = (file: File): string | null => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Syncing with the browser's object-URL registry (and an async decode) — outside React.
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const resolve = async () => {
      const source = isHEIC(file) ? await convertHEIC(file) : file.type.startsWith("image/") ? file : null;
      if (cancelled || !source) return;
      objectUrl = URL.createObjectURL(source);
      setPreviewUrl(objectUrl);
    };

    resolve();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
    };
  }, [file]);

  return previewUrl;
};
